import { defineStore } from "pinia";
import type { alertListEntry } from "@/alertlist";
import { getCookie, setCookie } from "@/helpers/cookies";
import { usePreferredLanguages, usePreferredReducedMotion } from "@vueuse/core";
import { i18n } from "@/locales";
import { EventEmitter } from "events";

export const useApplicationStore = defineStore("applicationStore", {
  state: () => ({
    alertList: [] as alertListEntry[],
    theme: "light" as "dark" | "light",
    language: "en" as "de" | "en",
    reducedMotion: false,
    skipEntryAnimation: false,
    displayTab: "print" as displayStateType,
    triggerPdfSearch: new EventEmitter(),
    sideNavbarOpen: false,
  }),
  actions: {
    addAlert(alert: alertListEntry) {
      // check if alert is already in it. remove it
      const foundIndex = this.alertList.findIndex((e) => e.key === alert.key);
      if (foundIndex !== -1) {
        this.alertList.splice(foundIndex, 1);
      }

      this.alertList.push({ ...alert, date: new Date() });
    },
    removeAlert(alert: alertListEntry) {
      const foundIndex = this.alertList.findIndex((e) => e === alert);
      if (foundIndex === -1) return;
      this.alertList.splice(foundIndex, 1);
    },
    setTheme(theme: "dark" | "light") {
      this.theme = theme;
    },
    setLanguage(lang: "de" | "en") {
      this.language = lang;

      i18n.global.locale.value = lang === "de" ? "de-DE" : "en-EN";
      document.title = i18n.global.t("app.name");
      setCookie("language", lang, 30);
      document.documentElement.setAttribute("lang", lang);
    },
    setSkipEntryAnimation(skip: boolean) {
      this.skipEntryAnimation = skip;
      setCookie("skip-entry-animation", String(skip), 30);
    },
    initState() {
      // animation
      const skipEntryAnimation = getCookie("skip-entry-animation");
      this.skipEntryAnimation = skipEntryAnimation == "true";
      // reduced motion
      this.reducedMotion = usePreferredReducedMotion().value === "reduce";
      // lang
      const lang = getCookie("language");
      if (lang) {
        this.setLanguage(lang === "de" ? "de" : "en");
        return;
      }
      const preferredLanguages = usePreferredLanguages();
      if (preferredLanguages.value.length === 0) {
        this.setLanguage("en");
        return;
      }
      this.setLanguage(
        preferredLanguages.value[0].includes("de") ? "de" : "en"
      );
    },
  },
});
